import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useNotification } from '../../context/NotificationContext';
import './FlyerAnalysis.css';
import { getCountryName, sortCountries, ALL_COUNTRIES } from './countries';

const API_BASE = process.env.REACT_APP_SERVER_DOMAIN;

const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
const MAX_FILES = 50;
const MAX_SIZE = 20 * 1024 * 1024; // 20MB

export default function FlyerAnalysisNewJob() {
  const { getAccessTokenSilently } = useAuth0();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const fileInputRef = useRef(null);
  const pollRef = useRef(null);

  const [activeTab, setActiveTab] = useState('s3');
  const [countries, setCountries] = useState(ALL_COUNTRIES);
  const [country, setCountry] = useState(() => searchParams.get('country') || localStorage.getItem('fa_country') || '');
  const [retailers, setRetailers] = useState([]);
  const [retailerSlug, setRetailerSlug] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // S3 Preview
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  // Upload
  const [files, setFiles] = useState([]);
  const [uploadRetailerName, setUploadRetailerName] = useState('');
  const [uploadFlyerDate, setUploadFlyerDate] = useState('');
  const [dragging, setDragging] = useState(false);

  // Job Progress
  const [activeJob, setActiveJob] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Fetch countries
  useEffect(() => {
    (async () => {
      try {
        const token = await getAccessTokenSilently();
        const res = await axios.get(`${API_BASE}/api/flyer-analysis/s3/countries`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setCountries(sortCountries(res.data.countries || []));
      } catch (err) {
        console.error('Error fetching countries:', err);
      }
    })();
  }, [getAccessTokenSilently]);

  // Fetch retailers when country changes
  useEffect(() => {
    if (!country) return;
    (async () => {
      try {
        const token = await getAccessTokenSilently();
        const res = await axios.get(`${API_BASE}/api/flyer-analysis/s3/retailers?country=${country}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setRetailers(res.data.retailers || []);
      } catch (err) {
        console.error('Error fetching retailers:', err);
      }
    })();
  }, [getAccessTokenSilently, country]);

  // Poll job progress — stop polling when paused or in a terminal state
  useEffect(() => {
    if (!activeJob || ['completed', 'failed', 'cancelled', 'paused'].includes(activeJob.status)) {
      if (pollRef.current) clearInterval(pollRef.current);
      return;
    }
    pollRef.current = setInterval(async () => {
      try {
        const token = await getAccessTokenSilently();
        const res = await axios.get(`${API_BASE}/api/flyer-analysis/jobs/${activeJob._id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setActiveJob(res.data);
        if (['completed', 'failed', 'cancelled', 'paused'].includes(res.data.status)) {
          clearInterval(pollRef.current);
          if (res.data.status === 'completed') {
            showNotification('Analysis job completed!', 'success');
          }
        }
      } catch (err) {
        console.error('Error polling job:', err);
      }
    }, 3000);
    return () => clearInterval(pollRef.current);
  }, [activeJob, getAccessTokenSilently, showNotification]);

  const handlePreview = async () => {
    if (!country) return;
    setLoadingPreview(true);
    try {
      const token = await getAccessTokenSilently();
      const params = new URLSearchParams({ country, limit: '100' });
      if (retailerSlug) params.set('retailer_slug', retailerSlug);
      if (dateFrom) params.set('date_from', dateFrom);
      if (dateTo) params.set('date_to', dateTo);
      const res = await axios.get(`${API_BASE}/api/flyer-analysis/s3/preview?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPreview(res.data);
    } catch (err) {
      console.error('Error fetching preview:', err);
      showNotification('Error loading preview', 'error');
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleSubmitS3 = async () => {
    if (!country) return;
    setSubmitting(true);
    try {
      const token = await getAccessTokenSilently();
      const body = { country };
      if (retailerSlug) body.retailer_slug = retailerSlug;
      if (dateFrom) body.date_from = dateFrom;
      if (dateTo) body.date_to = dateTo;
      const res = await axios.post(`${API_BASE}/api/flyer-analysis/jobs/s3-import`, body, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setActiveJob({ _id: res.data.job_id, status: 'queued', progress: {} });
      setPreview(null);
      showNotification('Job submitted!', 'success');
    } catch (err) {
      console.error('Error submitting job:', err);
      showNotification('Error submitting job', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelJob = async () => {
    if (!activeJob) return;
    try {
      const token = await getAccessTokenSilently();
      await axios.post(`${API_BASE}/api/flyer-analysis/jobs/${activeJob._id}/cancel`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setActiveJob(prev => ({ ...prev, status: 'cancelled' }));
      showNotification('Job cancelled', 'info');
    } catch (err) {
      console.error('Error cancelling job:', err);
    }
  };

  const handlePauseJob = async () => {
    if (!activeJob) return;
    try {
      const token = await getAccessTokenSilently();
      await axios.post(`${API_BASE}/api/flyer-analysis/jobs/${activeJob._id}/pause`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setActiveJob(prev => ({ ...prev, status: 'paused' }));
      showNotification('Job paused', 'info');
    } catch (err) {
      console.error('Error pausing job:', err);
    }
  };

  const handleResumeJob = async () => {
    if (!activeJob) return;
    try {
      const token = await getAccessTokenSilently();
      await axios.post(`${API_BASE}/api/flyer-analysis/jobs/${activeJob._id}/resume`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setActiveJob(prev => ({ ...prev, status: 'processing' }));
      showNotification('Job resumed', 'info');
    } catch (err) {
      console.error('Error resuming job:', err);
    }
  };

  // File upload handlers
  const handleFileDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const droppedFiles = Array.from(e.dataTransfer.files).filter(f =>
      ACCEPTED_TYPES.includes(f.type) && f.size <= MAX_SIZE
    );
    setFiles(prev => [...prev, ...droppedFiles].slice(0, MAX_FILES));
  };

  const handleFileSelect = (e) => {
    const selected = Array.from(e.target.files).filter(f =>
      ACCEPTED_TYPES.includes(f.type) && f.size <= MAX_SIZE
    );
    setFiles(prev => [...prev, ...selected].slice(0, MAX_FILES));
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleUploadSubmit = async () => {
    if (!country || files.length === 0) return;
    setSubmitting(true);
    try {
      const token = await getAccessTokenSilently();
      const formData = new FormData();
      formData.append('country', country);
      if (uploadRetailerName) formData.append('retailer_name', uploadRetailerName);
      if (uploadFlyerDate) formData.append('flyer_date', uploadFlyerDate);
      files.forEach(f => formData.append('files', f));
      const res = await axios.post(`${API_BASE}/api/flyer-analysis/jobs/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });
      setActiveJob({ _id: res.data.job_id, status: 'queued', progress: {} });
      setFiles([]);
      showNotification(`${res.data.files_uploaded} files uploaded!`, 'success');
    } catch (err) {
      console.error('Error uploading:', err);
      showNotification('Error uploading files', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1048576).toFixed(1)} MB`;
  };

  const jobProgress = activeJob?.progress || {};
  const jobPct = jobProgress.total_images > 0
    ? Math.round((jobProgress.processed / jobProgress.total_images) * 100)
    : 0;

  return (
    <div className="main-column" style={{ display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div className="fa-header">
        <div className="fa-header-left">
          <button className="fa-btn outline-gray" onClick={() => navigate('/flyer-analysis')}>
            ← Back
          </button>
          <div>
            <h1 className="fa-header-title">New Analysis Job</h1>
            <p className="fa-header-subtitle">Submit flyer images for AI analysis</p>
          </div>
        </div>
      </div>

      {/* Sub-navigation */}
      <div className="fa-subnav">
        <a href="/flyer-analysis" className="fa-subnav-link">Dashboard</a>
        <a href="/flyer-analysis/new-job" className="fa-subnav-link active">New Job</a>
        <a href={`/flyer-analysis/browse?country=${country}`} className="fa-subnav-link">Browse Flyers</a>
        <a href={`/flyer-analysis/insights?country=${country}`} className="fa-subnav-link">Insights</a>
      </div>

      <div className="fa-scroll-content">
        {/* Active Job Progress */}
        {activeJob && !['completed', 'failed', 'cancelled'].includes(activeJob.status) && (
          <div className="fa-job-progress">
            <div className="fa-job-progress-header">
              <h3 className="fa-job-progress-title">
                {activeJob.status === 'paused' ? 'Job Paused' : 'Job in Progress'}
                <span className={`fa-status ${activeJob.status}`} style={{ marginLeft: 12 }}>{activeJob.status}</span>
              </h3>
              <div style={{ display: 'flex', gap: 8 }}>
                {activeJob.status === 'paused' ? (
                  <button className="fa-btn primary" onClick={handleResumeJob}>Resume</button>
                ) : (
                  <button className="fa-btn outline" onClick={handlePauseJob}>Pause</button>
                )}
                <button className="fa-btn danger" onClick={handleCancelJob}>Cancel</button>
              </div>
            </div>
            <div className="fa-progress-wrapper">
              <div className="fa-progress-track" style={{ height: 12 }}>
                <div className="fa-progress-fill" style={{ width: `${jobPct}%`, height: 12 }} />
              </div>
              <span className="fa-progress-text">{jobPct}%</span>
            </div>
            <div className="fa-job-progress-stats">
              <div className="fa-job-progress-stat">
                <div className="fa-job-progress-stat-value">{jobProgress.processed || 0}</div>
                <div className="fa-job-progress-stat-label">Processed</div>
              </div>
              <div className="fa-job-progress-stat">
                <div className="fa-job-progress-stat-value" style={{ color: '#16a34a' }}>{jobProgress.succeeded || 0}</div>
                <div className="fa-job-progress-stat-label">Succeeded</div>
              </div>
              <div className="fa-job-progress-stat">
                <div className="fa-job-progress-stat-value" style={{ color: '#dc2626' }}>{jobProgress.failed || 0}</div>
                <div className="fa-job-progress-stat-label">Failed</div>
              </div>
              {activeJob.estimated_cost_usd != null && (
                <div className="fa-job-progress-stat">
                  <div className="fa-job-progress-stat-value">${activeJob.estimated_cost_usd.toFixed(2)}</div>
                  <div className="fa-job-progress-stat-label">Est. Cost</div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Completed/Failed Job Summary */}
        {activeJob && ['completed', 'failed', 'cancelled'].includes(activeJob.status) && (
          <div className="fa-info-banner" style={{
            background: activeJob.status === 'completed' ? '#f0fdf4' : activeJob.status === 'failed' ? '#fef2f2' : '#fef9c3',
            borderColor: activeJob.status === 'completed' ? '#bbf7d0' : activeJob.status === 'failed' ? '#fecaca' : '#fde68a',
          }}>
            <p style={{
              color: activeJob.status === 'completed' ? '#16a34a' : activeJob.status === 'failed' ? '#dc2626' : '#ca8a04',
            }}>
              Job {activeJob.status}
              {jobProgress.succeeded > 0 && ` — ${jobProgress.succeeded} pages analyzed successfully`}
              {jobProgress.failed > 0 && `, ${jobProgress.failed} failed`}
              {activeJob.estimated_cost_usd != null && ` — $${activeJob.estimated_cost_usd.toFixed(2)} est. cost`}
            </p>
          </div>
        )}

        {/* Tab Switcher */}
        <div className="fa-tabs">
          <button className={`fa-tab ${activeTab === 's3' ? 'active' : ''}`} onClick={() => setActiveTab('s3')}>
            S3 Import
          </button>
          <button className={`fa-tab ${activeTab === 'upload' ? 'active' : ''}`} onClick={() => setActiveTab('upload')}>
            Upload Files
          </button>
        </div>

        {/* S3 Import Tab */}
        {activeTab === 's3' && (
          <div className="fa-form-section">
            <div className="fa-form-grid">
              <div className="fa-form-group">
                <label className="fa-form-label">Country *</label>
                <select className="fa-form-select" value={country} onChange={(e) => {
                  setCountry(e.target.value);
                  localStorage.setItem('fa_country', e.target.value);
                  setRetailerSlug('');
                  setPreview(null);
                }}>
                  <option value="" disabled>Select Country</option>
                  {countries.map(c => <option key={c} value={c}>{getCountryName(c)}</option>)}
                </select>
              </div>
              <div className="fa-form-group">
                <label className="fa-form-label">Retailer (optional)</label>
                <select className="fa-form-select" value={retailerSlug} onChange={(e) => {
                  setRetailerSlug(e.target.value);
                  setPreview(null);
                }}>
                  <option value="">All Retailers</option>
                  {retailers.map(r => <option key={r.slug} value={r.slug}>{r.slug}</option>)}
                </select>
              </div>
              <div className="fa-form-group">
                <label className="fa-form-label">Date From</label>
                <input type="date" className="fa-form-input" value={dateFrom} onChange={(e) => {
                  setDateFrom(e.target.value);
                  setPreview(null);
                }} />
              </div>
              <div className="fa-form-group">
                <label className="fa-form-label">Date To</label>
                <input type="date" className="fa-form-input" value={dateTo} onChange={(e) => {
                  setDateTo(e.target.value);
                  setPreview(null);
                }} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 12, marginTop: 16 }}>
              <button className="fa-btn outline" onClick={handlePreview} disabled={!country || loadingPreview}>
                {loadingPreview ? 'Loading...' : 'Preview'}
              </button>
              <button className="fa-btn primary" onClick={handleSubmitS3} disabled={!country || submitting}>
                {submitting ? 'Submitting...' : 'Start Analysis'}
              </button>
            </div>

            {/* Preview Results */}
            {preview && (
              <div style={{ marginTop: 24 }}>
                <div className="fa-preview-summary">
                  <div className="fa-preview-stat">
                    <div className="fa-preview-stat-value">{preview.total_found}</div>
                    <div className="fa-preview-stat-label">Total Found</div>
                  </div>
                  <div className="fa-preview-stat">
                    <div className="fa-preview-stat-value" style={{ color: '#16a34a' }}>{preview.new_to_process}</div>
                    <div className="fa-preview-stat-label">New to Process</div>
                  </div>
                  <div className="fa-preview-stat">
                    <div className="fa-preview-stat-value" style={{ color: '#6b7280' }}>{preview.already_processed}</div>
                    <div className="fa-preview-stat-label">Already Processed</div>
                  </div>
                </div>

                {preview.images?.length > 0 && (
                  <div className="fa-table-wrapper">
                    <table className="fa-table">
                      <thead>
                        <tr>
                          <th>Retailer</th>
                          <th>Date</th>
                          <th>Page</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {preview.images.map((img, i) => (
                          <tr key={i}>
                            <td style={{ fontWeight: 500 }}>{img.retailer_name || img.retailer_slug}</td>
                            <td style={{ color: '#6b7280' }}>{img.flyer_date || '—'}</td>
                            <td>{img.page_number || '—'}</td>
                            <td>
                              <span className={`fa-badge ${img.already_processed ? 'gray' : 'green'}`}>
                                {img.already_processed ? 'Processed' : 'New'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Upload Tab */}
        {activeTab === 'upload' && (
          <div className="fa-form-section">
            <div className="fa-form-grid">
              <div className="fa-form-group">
                <label className="fa-form-label">Country *</label>
                <select className="fa-form-select" value={country} onChange={(e) => {
                  setCountry(e.target.value);
                  localStorage.setItem('fa_country', e.target.value);
                }}>
                  <option value="" disabled>Select Country</option>
                  {countries.map(c => <option key={c} value={c}>{getCountryName(c)}</option>)}
                </select>
              </div>
              <div className="fa-form-group">
                <label className="fa-form-label">Retailer Name (optional)</label>
                <input type="text" className="fa-form-input" value={uploadRetailerName}
                  onChange={(e) => setUploadRetailerName(e.target.value)}
                  placeholder="e.g., WALMART" />
              </div>
              <div className="fa-form-group" style={{ gridColumn: '1 / -1' }}>
                <label className="fa-form-label">Flyer Date (optional)</label>
                <input type="date" className="fa-form-input" value={uploadFlyerDate}
                  onChange={(e) => setUploadFlyerDate(e.target.value)} style={{ maxWidth: 240 }} />
              </div>
            </div>

            {/* Dropzone */}
            <div
              className={`fa-dropzone ${dragging ? 'active' : ''}`}
              style={{ marginTop: 16 }}
              onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={handleFileDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <div className="fa-dropzone-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="16 16 12 12 8 16"/><line x1="12" y1="12" x2="12" y2="21"/>
                  <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3"/>
                </svg>
              </div>
              <p className="fa-dropzone-text"><span>Click to upload</span> or drag and drop</p>
              <p className="fa-dropzone-hint">JPG, PNG, WebP, PDF — max {MAX_FILES} files, {MAX_SIZE / 1048576}MB each</p>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept=".jpg,.jpeg,.png,.webp,.pdf"
                style={{ display: 'none' }}
                onChange={handleFileSelect}
              />
            </div>

            {/* File List */}
            {files.length > 0 && (
              <div className="fa-file-list">
                <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 8 }}>
                  {files.length} file{files.length !== 1 ? 's' : ''} selected
                </div>
                {files.map((f, i) => (
                  <div key={i} className="fa-file-item">
                    <span className="fa-file-name">{f.name}</span>
                    <span className="fa-file-size">{formatSize(f.size)}</span>
                    <button className="fa-file-remove" onClick={() => removeFile(i)}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                      </svg>
                    </button>
                  </div>
                ))}
                <div style={{ marginTop: 16 }}>
                  <button className="fa-btn primary" onClick={handleUploadSubmit} disabled={!country || submitting}>
                    {submitting ? 'Uploading...' : `Upload & Analyze (${files.length} files)`}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
